/**
 * Modo presentación: quien presenta arrastra la vista de los demás.
 *
 * Al tocar "Presentar" se anuncia por presencia (`presenting: true`) y todas las
 * pantallas conectadas adoptan esa cámara. La cámara ajena no se copia de golpe
 * —se ve a tirones— sino que se persigue por interpolación cada cuadro.
 *
 * Si el espectador mueve la vista con su propia mano, el seguimiento se pausa
 * un rato para dejarlo mirar y luego vuelve solo. Con "Salir" se sale del todo
 * hasta que empiece otra presentación.
 */
import type { Presence } from '../shared/types.ts'
import type { Camera, Store } from './state/store.ts'
import { chaseCamera } from './lib/camera.ts'

/** Cuánto se espera tras mover la vista propia antes de volver a seguir. */
const RESUME_AFTER_MS = 20_000
/**
 * Margen antes de dar por ido al que presenta. La presencia se borra sola a los
 * 30 s sin noticias y una pestaña en segundo plano manda mucho menos, así que
 * soltarse al primer hueco dejaba al público plantado en mitad de una demo.
 */
const LOST_GRACE_MS = 12_000

export type FollowMode = 'manual' | 'auto' | null

export interface PresentationHost {
  store: Store
  userId: string
  peers(): Presence[]
  publish(field: string, value: unknown): void
  toast(message: string): void
  /** avisa a la interfaz de que hay que repintar el aviso */
  onChange(): void
}

export class Presentation {
  /** este usuario está presentando */
  presenting = false
  /** cómo llegamos a seguir a alguien: a mano o por una presentación */
  mode: FollowMode = null
  /** el espectador se salió a propósito: no volver a engancharlo */
  private optOut = false
  /** movió la vista por su cuenta: no seguir hasta pasado este momento */
  private pausedUntil = 0
  /** desde cuándo no se ve al que seguimos (0 = está ahí) */
  private lostSince = 0
  private target: Camera | null = null
  private raf = 0
  private fallback: number | null = null
  private timer: number
  private host: PresentationHost

  constructor(host: PresentationHost) {
    this.host = host
    // El presentador puede quedarse quieto (no llegan avisos de presencia),
    // así que se revisa también con un latido lento.
    this.timer = window.setInterval(() => this.sync(), 1500)
  }

  dispose(): void {
    clearInterval(this.timer)
    this.clearStep()
  }

  /* ── Presentar ──────────────────────────────────────────── */

  get presenter(): Presence | null {
    return this.findPresenter(this.host.peers())
  }

  /**
   * Quien presenta, si no soy yo. Dos pestañas de la misma cuenta comparten
   * `userId`, y seguirse a uno mismo no lleva a ningún lado.
   */
  private findPresenter(peers: Presence[]): Presence | null {
    return peers.find((p) => p.presenting && p.id !== this.host.userId) ?? null
  }

  togglePresenting(): void {
    this.presenting ? this.stopPresenting() : this.startPresenting()
  }

  startPresenting(): void {
    // Presentar y seguir a la vez no tiene sentido: manda quien presenta.
    this.detach()
    this.presenting = true
    this.host.publish('presenting', true)
    // No se anuncia un número: los invitados de solo lectura siguen la
    // presentación pero el servidor no publica su presencia, así que no
    // aparecen en la cuenta y quedaría un número mentiroso.
    this.host.toast('Presentando: las demás pantallas siguen tu vista')
    this.host.onChange()
  }

  stopPresenting(): void {
    if (!this.presenting) return
    this.presenting = false
    this.host.publish('presenting', false)
    this.host.toast('Dejaste de presentar')
    this.host.onChange()
  }

  /* ── Seguir ─────────────────────────────────────────────── */

  /** Seguimiento a mano (al tocar el avatar de alguien). */
  toggleManualFollow(userId: string): void {
    if (this.host.store.followingUser === userId) {
      this.detach()
      this.host.toast('Dejaste de seguir')
    } else {
      const peer = this.host.peers().find((p) => p.id === userId)
      this.attach(userId, 'manual')
      this.host.toast(`Siguiendo a ${peer?.name ?? 'usuario'}`)
    }
    this.host.onChange()
  }

  /** Sale de la presentación: ya no se vuelve a enganchar solo. */
  leave(): void {
    this.optOut = true
    this.detach()
    this.host.toast('Saliste de la presentación. Vuelves con "Seguir".')
    this.host.onChange()
  }

  /** Vuelve a engancharse a quien esté presentando. */
  rejoin(): void {
    this.optOut = false
    this.pausedUntil = 0
    this.sync()
    this.host.onChange()
  }

  /**
   * El usuario movió la vista con su propia mano. Si estaba siguiendo una
   * presentación se pausa y vuelve solo; si seguía a alguien a mano, se suelta.
   */
  onUserCameraMove(): void {
    if (this.presenting || !this.host.store.followingUser) return
    if (this.mode === 'auto') {
      this.pausedUntil = Date.now() + RESUME_AFTER_MS
      this.detach()
    } else {
      this.detach()
    }
    this.host.onChange()
  }

  /** Se llama en cada cambio de presencia y con el latido. */
  sync(): void {
    const peers = this.host.peers()
    const following = this.host.store.followingUser

    // Desapareció de la lista: puede ser que se fuera o que solo lleve un rato
    // callado. Se le da un margen antes de soltarse.
    if (following && !peers.some((p) => p.id === following)) {
      if (!this.lostSince) this.lostSince = Date.now()
      if (Date.now() - this.lostSince > LOST_GRACE_MS) {
        this.lostSince = 0
        this.detach()
        this.host.onChange()
      }
      return
    }
    this.lostSince = 0

    const presenter = this.findPresenter(peers)

    // Se acabó la presentación (o se fue quien presentaba).
    if (!presenter) {
      if (this.mode === 'auto') this.detach()
      if (this.optOut || this.pausedUntil) {
        this.optOut = false
        this.pausedUntil = 0
        this.host.onChange()
      }
      // Seguir a alguien a mano sigue valiendo aunque nadie presente.
      if (this.mode === 'manual') this.chase(peers)
      return
    }

    if (!this.presenting && !this.optOut && Date.now() >= this.pausedUntil) {
      if (this.host.store.followingUser !== presenter.id) {
        this.attach(presenter.id, 'auto')
        this.host.toast(`${presenter.name} está presentando`)
        this.host.onChange()
      } else if (this.pausedUntil) {
        this.pausedUntil = 0
        this.host.onChange()
      }
    }
    this.chase(peers)
  }

  private attach(userId: string, mode: FollowMode): void {
    this.host.store.followingUser = userId
    this.mode = mode
    this.pausedUntil = 0
    this.lostSince = 0
    this.chase(this.host.peers())
  }

  private detach(): void {
    this.host.store.followingUser = null
    this.mode = null
    this.target = null
    this.clearStep()
    this.host.store.emit()
  }

  /** Apunta a la cámara del seguido y arranca la persecución. */
  private chase(peers: Presence[]): void {
    const id = this.host.store.followingUser
    if (!id) return
    // Si no está en la lista no se suelta aquí: de eso se encarga sync() con
    // su margen. Se sigue apuntando a la última vista conocida.
    const peer = peers.find((p) => p.id === id)
    if (!peer?.camera) return
    this.target = { ...peer.camera }
    this.scheduleStep()
  }

  /**
   * Pide el siguiente cuadro por dos vías a la vez y se queda con la primera.
   *
   * El navegador congela `requestAnimationFrame` en las pestañas que no están
   * al frente: sin el temporizador de relevo, una pantalla en segundo plano se
   * quedaba clavada en la vista vieja y al volver aparecía en otro sitio.
   */
  private scheduleStep(): void {
    if (this.raf || this.fallback !== null) return
    const run = (): void => {
      this.clearStep()
      this.step()
    }
    this.raf = requestAnimationFrame(run)
    this.fallback = window.setTimeout(run, 250)
  }

  private clearStep(): void {
    if (this.raf) cancelAnimationFrame(this.raf)
    if (this.fallback !== null) clearTimeout(this.fallback)
    this.raf = 0
    this.fallback = null
  }

  /** Un cuadro de persecución de la cámara del que presenta. */
  private step = (): void => {
    const target = this.target
    if (!target || !this.host.store.followingUser) return

    const cam = this.host.store.camera
    const { camera, done } = chaseCamera(cam, target)
    if (done) {
      if (cam.x !== target.x || cam.y !== target.y || cam.z !== target.z) {
        this.host.store.setCamera(camera)
      }
      return
    }
    this.host.store.setCamera(camera)
    this.scheduleStep()
  }
}
